import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scanner } from '@yudiel/react-qr-scanner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QRScanner = ({ isOpen, onClose, onScan, eventId, eventTitle }) => {
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [checkInResult, setCheckInResult] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setLastResult(null);
      setCheckInResult(null);
    }
  }, [isOpen]);

  const handleScan = async (result) => {
    if (!result || !result[0] || processing) return;
    
    const data = result[0].rawValue;
    
    // Prevent duplicate scans
    if (data === lastResult) return;
    setLastResult(data);
    setProcessing(true);
    
    // Parse QR code - accept WCLUB: or WCLUB-
    let memberId = null;
    if (data.startsWith('WCLUB:')) {
      memberId = data.replace('WCLUB:', '');
    } else if (data.startsWith('WCLUB-')) {
      memberId = data.replace('WCLUB-', '');
    }
    
    if (!memberId) {
      setCheckInResult({
        success: false,
        message: `QR inválido: ${data.substring(0, 30)}...`
      });
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      setTimeout(() => {
        setCheckInResult(null);
        setLastResult(null);
        setProcessing(false);
      }, 2500);
      return;
    }
    
    // Do check-in
    try {
      const token = localStorage.getItem('wclub_admin_token');
      if (!token) {
        setCheckInResult({
          success: false,
          message: 'Você precisa estar logado como admin'
        });
        setTimeout(() => {
          setCheckInResult(null);
          setLastResult(null);
          setProcessing(false);
        }, 2500);
        return;
      }
      
      const res = await axios.post(
        `${API}/events/${eventId}/checkin`,
        { member_id: memberId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCheckInResult({
        success: true,
        message: `${res.data.member_name} - Check-in realizado!`
      });
      
      if (navigator.vibrate) navigator.vibrate(200);
      
      // Callback
      if (onScan) onScan(res.data);
      
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao fazer check-in';
      setCheckInResult({
        success: false,
        message: msg
      });
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    }
    
    setTimeout(() => {
      setCheckInResult(null);
      setLastResult(null);
      setProcessing(false);
    }, 2500);
  };

  const handleError = (err) => {
    console.error('Scanner error:', err);
    if (err?.message?.includes('Permission')) {
      setError('Permissão de câmera negada');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex flex-col"
        style={{ background: 'linear-gradient(180deg, rgba(5,5,15,0.98) 0%, rgba(10,10,25,0.99) 100%)' }}
        data-testid="qr-scanner-modal"
      >
        {/* Header - Liquid Glass */}
        <div className="flex items-center justify-between p-4 backdrop-blur-xl"
          style={{
            background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(59,130,246,0.05) 100%)',
            borderBottom: '1px solid rgba(139,92,246,0.15)'
          }}>
          <div>
            <h2 className="text-white font-['Jost'] text-lg font-semibold" style={{ textShadow: '0 0 20px rgba(139,92,246,0.5)' }}>Scanner QR</h2>
            {eventTitle && (
              <p className="text-white/50 text-sm font-['Jost']">{eventTitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all backdrop-blur-xl"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
            data-testid="close-scanner-btn"
          >
            <svg className="w-5 h-5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scanner Area */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="relative w-full max-w-sm aspect-square rounded-3xl overflow-hidden"
            style={{
              background: 'rgba(0,0,0,0.5)',
              border: '2px solid rgba(139,92,246,0.3)',
              boxShadow: '0 0 40px rgba(139,92,246,0.2), inset 0 0 40px rgba(0,0,0,0.5)'
            }}>
            {error ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-4">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{
                      background: 'rgba(239,68,68,0.15)',
                      border: '2px solid rgba(239,68,68,0.3)',
                      boxShadow: '0 0 30px rgba(239,68,68,0.3)'
                    }}>
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                      style={{ color: '#ef4444', filter: 'drop-shadow(0 0 10px rgba(239,68,68,0.8))' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <p className="text-white/70 text-sm font-['Jost']">{error}</p>
                </div>
              </div>
            ) : (
              <Scanner
                onScan={handleScan}
                onError={handleError}
                constraints={{
                  facingMode: 'environment'
                }}
                formats={['qr_code']}
                components={{
                  audio: false,
                  finder: true
                }}
                styles={{
                  container: {
                    width: '100%',
                    height: '100%'
                  },
                  video: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }
                }}
              />
            )}
            
            {/* Scan frame overlay - Liquid Glass corners */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-6">
                <div className="absolute -top-0.5 -left-0.5 w-10 h-10 border-t-4 border-l-4 rounded-tl-2xl"
                  style={{ borderColor: 'rgba(139,92,246,0.8)', filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.6))' }} />
                <div className="absolute -top-0.5 -right-0.5 w-10 h-10 border-t-4 border-r-4 rounded-tr-2xl"
                  style={{ borderColor: 'rgba(139,92,246,0.8)', filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.6))' }} />
                <div className="absolute -bottom-0.5 -left-0.5 w-10 h-10 border-b-4 border-l-4 rounded-bl-2xl"
                  style={{ borderColor: 'rgba(139,92,246,0.8)', filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.6))' }} />
                <div className="absolute -bottom-0.5 -right-0.5 w-10 h-10 border-b-4 border-r-4 rounded-br-2xl"
                  style={{ borderColor: 'rgba(139,92,246,0.8)', filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.6))' }} />
              </div>
              
              {/* Scanning line animation */}
              <motion.div
                animate={{ y: [0, 200, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute left-6 right-6 h-1 rounded-full"
                style={{ 
                  top: '1.5rem',
                  background: 'linear-gradient(90deg, transparent 0%, rgba(139,92,246,0.8) 50%, transparent 100%)',
                  boxShadow: '0 0 20px rgba(139,92,246,0.6)'
                }}
              />
            </div>
          </div>
        </div>

        {/* Result toast - Liquid Glass with illuminated icons */}
        <AnimatePresence>
          {checkInResult && (
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.9 }}
              className="absolute bottom-28 left-4 right-4 p-5 rounded-3xl backdrop-blur-xl"
              style={checkInResult.success ? {
                background: 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(16,185,129,0.1) 100%)',
                border: '1px solid rgba(34,197,94,0.3)',
                boxShadow: '0 0 40px rgba(34,197,94,0.2)'
              } : {
                background: 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.1) 100%)',
                border: '1px solid rgba(239,68,68,0.3)',
                boxShadow: '0 0 40px rgba(239,68,68,0.2)'
              }}
            >
              <div className="flex items-center gap-4">
                {checkInResult.success ? (
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'rgba(34,197,94,0.2)',
                      border: '2px solid rgba(34,197,94,0.4)',
                      boxShadow: '0 0 25px rgba(34,197,94,0.5)'
                    }}>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}
                      style={{ color: '#4ade80', filter: 'drop-shadow(0 0 8px rgba(34,197,94,0.8))' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'rgba(239,68,68,0.2)',
                      border: '2px solid rgba(239,68,68,0.4)',
                      boxShadow: '0 0 25px rgba(239,68,68,0.5)'
                    }}>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}
                      style={{ color: '#f87171', filter: 'drop-shadow(0 0 8px rgba(239,68,68,0.8))' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                <p className={`text-base font-['Jost'] font-medium ${checkInResult.success ? 'text-green-300' : 'text-red-300'}`}
                  style={{ textShadow: checkInResult.success ? '0 0 10px rgba(34,197,94,0.5)' : '0 0 10px rgba(239,68,68,0.5)' }}>
                  {checkInResult.message}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Instructions - Liquid Glass */}
        <div className="p-5 text-center backdrop-blur-xl"
          style={{
            background: 'linear-gradient(180deg, rgba(139,92,246,0.05) 0%, rgba(59,130,246,0.03) 100%)',
            borderTop: '1px solid rgba(139,92,246,0.1)'
          }}>
          <p className="text-white/50 text-sm font-['Jost']">
            Aponte a câmera para o QR Code do cartão WClub
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QRScanner;
